import type { SVGProps } from "react"
import { PageHero } from "@/components/PageHero"
import { Reveal } from "@/components/Reveal"
import { RemoteImage } from "@/components/RemoteImage"
import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { team } from "@/data/site"

function LinkedinIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

export default function Team() {
  return <>
    <PageHero
      eyebrow="Urban Inventors Pty Ltd"
      title="The people engineering Smart Drain."
      description={
        <>
          <span className="block font-medium text-[#669c38] mb-3">
            Urban Inventors Pty Ltd is a hardware-enabled infrastructure intelligence company.
          </span>

          <span>
            A multidisciplinary team spanning urban water, drainage, IoT,
            product design, data innovation and structural engineering.
          </span>
        </>
      }
    />
    <section className="px-6 py-24 sm:py-32">
      <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {team.map((person, i) => (
          <Reveal key={person.name} delay={i * .04}>
            <Card className="group h-full overflow-hidden">
              <div className="overflow-hidden bg-slate-100 dark:bg-white/[.06]">
                <RemoteImage src={person.image} alt={person.name} className="aspect-[3/4] object-[center_20%] transition duration-500 group-hover:scale-[1.025]" />
              </div>
              <div className="p-6">
                <Badge className="text-emerald-800 dark:text-emerald-300">{person.role}</Badge>
                <h2 className="mt-4 text-2xl font-semibold tracking-[-.04em]">{person.name}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">{person.bio}</p>
                {person.linkedin && (
                  <a
                    href={person.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 transition-colors hover:text-emerald-600 dark:text-emerald-300 dark:hover:text-emerald-200"
                    aria-label={`${person.name} on LinkedIn`}
                  >
                    <LinkedinIcon className="size-4" />
                    LinkedIn
                  </a>
                )}
              </div>
            </Card>
          </Reveal>
        ))}
      </div>
    </section>
  </>
}
